import { captureException } from "@sentry/react"
import { toast } from "@suraasa/placebo-ui"
import api from "api"
import { SUBMISSION_STATUS } from "api/resources/assignments/types"
import { RestrictionType } from "api/resources/learningItems/types"
import { PaymentMethod } from "api/resources/payment/types"
import { APIError } from "api/utils"
import { format } from "date-fns"
import get from "lodash/get"
import { FieldValues, UseFormSetError } from "react-hook-form"
import routes from "routes"

import {
  BROWSER_STORAGE_KEYS,
  DEFAULT_LANGUAGE_CODE,
  USER_TYPE,
} from "./constants"
import { ValueOf } from "./types"

export const formatDate = (
  date: string | undefined,
  formatStr = "MMMM d, yyyy"
) => (date ? format(new Date(date), formatStr) : "")

// check session and local storage support
export function isStorageSupported() {
  // try {
  //   localStorage.setItem("__TEST_LOCAL_STORAGE", "true")
  //   localStorage.removeItem("__TEST_LOCAL_STORAGE")
  //   sessionStorage.setItem("__TEST_SESSION_STORAGE", "true")
  //   sessionStorage.removeItem("__TEST_SESSION_STORAGE")
  // } catch (e) {
  //   // alerting the user to prevent further errors or App issues
  //   alert(
  //     "Your browser has cookies disabled. Make sure that your cookies are enabled. Press OK to reload"
  //   )
  //   // reload browser to prevent further error popups and check if user enabled storage
  //   window.location.reload()
  //   return false
  // }
  // returns true if storages are supported
  return true
}

export function getUserLanguage() {
  const { language } = BROWSER_STORAGE_KEYS
  // checking storage access before accessing default language
  return isStorageSupported()
    ? localStorage.getItem(language) || DEFAULT_LANGUAGE_CODE
    : DEFAULT_LANGUAGE_CODE
}

export function isUUIDString(str: string) {
  const UUIDv4Regex =
    /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
  return UUIDv4Regex.test(str)
}

export function pluralize(
  word: string,
  count: number,
  {
    endsWithVowel,
    skipCount,
    plural,
  }: {
    endsWithVowel?: boolean
    skipCount?: boolean
    plural?: string
  } = {
    endsWithVowel: false,
    skipCount: false,
    plural: "",
  }
) {
  let str = `${count} `

  if (skipCount) {
    str = ""
  }

  if (plural) {
    return `${count !== 1 ? `${str}${plural}` : `${str}${word}`}`
  }

  return `${
    count !== 1 ? `${str}${word}${endsWithVowel ? "es" : "s"}` : `${str}${word}`
  }`
}

export const buildUserName = (user: {
  firstName: string
  lastName: string | null
}) => {
  return [user.firstName, user.lastName].filter(Boolean).join(" ")
}

export const generateObjectURL = (file: File) => {
  const URL = window.URL || window.webkitURL
  return URL.createObjectURL(file)
}

/**
 * @param setter setter to set errors in the form
 * @param fieldErrors errors from the API
 * @param fields Format: [BackendKey, FrontendKey] OR [key] (if both key names are same)
 * @deprecated Use `mapErrorsV2` instead
 */
export const mapErrors = <T extends FieldValues>(
  setter: UseFormSetError<T>,
  err: any,
  fields: ([string, string] | [string])[]
) => {
  if (!narrowError(err)) {
    throw new Error(
      "Error supplied to mapErrors is not an instance of APIError. Throwing original error",
      err
    )
  }

  const {
    errors: { fieldErrors, message },
  } = err

  if (message) {
    toast.error(message)
  }

  fields.forEach(pair => {
    if (!fieldErrors) return

    const key = pair[0]
    const message = Array.isArray(fieldErrors[key])
      ? fieldErrors[key].join("; ")
      : fieldErrors[key]

    if (pair.length === 1) {
      if (message) {
        setter(
          key as any,
          {
            message: message as string,
          },
          { shouldFocus: true }
        )
      }
    }
    if (pair.length === 2) {
      const frontendKey = pair[1]
      if (message) {
        setter(
          frontendKey as any,
          { message: message as string },
          { shouldFocus: true }
        )
      }
    }
  })
}

/**
 * [backendKey, frontendKey] OR [key] (if both key names are same)
 */
export const mapErrorsV2 = <T extends FieldValues>(
  err: any,
  options?: {
    setter?: UseFormSetError<T>
    fields: ([string, string] | [string])[]
  }
) => {
  if (!narrowError(err)) {
    captureException(err, scope => {
      scope.setTag("source", "map_errors_v2")
      return scope
    })
    toast.error("An unexpected error occurred!", {
      description: "Our team has been notified",
    })
    throw new Error(err)
  }

  if (!err.statusCode) {
    toast.error("Please check your internet connection!", {
      description: "Try refreshing the page",
    })
    return
  }

  const {
    errors: { fieldErrors, message },
  } = err

  if (message) {
    toast.error(message)
  }
  if (err.is500) {
    toast.error("Something went wrong.", {
      description: "Our team has been notified",
    })
  }

  if (fieldErrors && !options?.setter) {
    console.warn("> No setter provided to map field errors")
    const firstError = Object.entries(fieldErrors)[0]
    if (firstError) {
      const [k, v] = firstError
      toast.error(`Error on key '${k}'`, {
        description: typeof v === "string" ? v : JSON.stringify(v[0]),
      })
    }
  }

  if (!options) return
  const setter = options.setter

  if (!options?.fields || !setter) return

  options.fields.forEach(pair => {
    if (!fieldErrors) return

    const key = pair[0]
    const errorMsg = get(fieldErrors, key)
    const message = Array.isArray(errorMsg)
      ? (errorMsg as string[]).join("; ")
      : errorMsg
    if (pair.length === 1) {
      if (message) {
        setter(
          key as any,
          {
            message: message as string,
          },
          { shouldFocus: true }
        )
      }
    }
    if (pair.length === 2) {
      const frontendKey = pair[1]
      if (message) {
        setter(
          frontendKey as any,
          { message: message as string },
          { shouldFocus: true }
        )
      }
    }
  })
}

/**
 * @deprecated Use `mapErrorsV2` instead
 */
export const handleErrors = <T extends FieldValues>(
  errors: APIError,
  options?: {
    setter?: UseFormSetError<T>
  }
) => {
  const {
    errors: { message, fieldErrors },
  } = errors
  if (message) {
    toast.error(message)
  }

  if (options?.setter && fieldErrors) {
    for (const [k, v] of Object.entries(fieldErrors)) {
      if (v)
        options.setter(k as any, { message: typeof v === "string" ? v : v[0] })
    }
    return
  }

  // if there was no message, but there were fieldErrors without a setter, we need to show them to the user
  if (!message && fieldErrors && !options?.setter) {
    const firstError = Object.entries(fieldErrors)[0]
    if (firstError) {
      const [k, v] = firstError
      toast.error(`Error on key '${k}'`, {
        description: typeof v === "string" ? v : JSON.stringify(v[0]),
      })
    }
  }
}

export const saveBlobAsFile = ({
  data,
  type,
  name,
}: {
  data: any
  type: string
  name: string
}) => {
  const blob = new Blob([data], { type })
  const blobData = window.URL.createObjectURL(blob)
  downloadFile(blobData, name)
}

// The backend returns a null invoicePdf until the receipt has been generated
export const downloadInvoice = ({
  invoicePdf,
  invoiceName,
}: {
  invoicePdf: string | null
  invoiceName: string
}) => {
  if (!invoicePdf) {
    toast.error(
      "Your receipt is still being generated. Please try again in a few minutes."
    )
    return
  }
  downloadFile(invoicePdf, invoiceName)
}

export const downloadFile = (
  href: string,
  name: string,
  { skipBlockedFallback = false }: { skipBlockedFallback?: boolean } = {}
) => {
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )

  if (isMobile) {
    const newWindow = window.open(href, "_blank")

    // If the popup was blocked, window.location.href navigates the current
    // tab to the file — fine for a direct click, but callers looping over
    // multiple downloads (e.g. "Download all") must opt out, since later
    // iterations lose the click's user activation and would otherwise
    // navigate away from the page mid-loop. Those callers get a toast
    // instead so the skipped download isn't a silent no-op.
    if (!newWindow) {
      if (skipBlockedFallback) {
        toast.error(`Couldn't download "${name}". Please try again.`)
      } else {
        window.location.href = href
      }
    }

    setTimeout(() => {
      if (href.startsWith("blob:")) {
        window.URL.revokeObjectURL(href)
      }
    }, 1000)
    return
  }

  const link = document.createElement("a")
  link.href = href
  link.download = name
    ? name.replace(/[^a-zA-Z0-9-_. ]/g, "")
    : `file_download_${new Date().getTime()}`
  link.target = "_blank"
  link.click()
  setTimeout(() => {
    window.URL.revokeObjectURL(href)
  }, 100)
}

export const openFile = (href: string) => {
  const link = document.createElement("a")
  link.href = href
  link.target = "_blank"
  link.click()
  setTimeout(() => {
    window.URL.revokeObjectURL(href)
  }, 100)
}

export function convertHexToRGBA(hex: string, opacity: number) {
  const tempHex: string = hex.replace("#", "")

  const r = parseInt(tempHex.substring(0, 2), 16)
  const g = parseInt(tempHex.substring(2, 4), 16)
  const b = parseInt(tempHex.substring(4, 6), 16)

  return `rgba(${r},${g},${b},${opacity / 100})`
}

export function narrowError(e: unknown): e is APIError {
  return e instanceof APIError
}

export const getPlatformURL = (
  platform: "jobs" | "suraasa" | "sso" | "hire" | "itu",
  url: string
) => {
  let domain

  if (platform === "hire") {
    domain = import.meta.env.VITE_HIRE_PLATFORM_URL
  }
  if (platform === "jobs") {
    domain = import.meta.env.VITE_JOBS_PLATFORM_URL
  }
  if (platform === "suraasa") {
    domain = import.meta.env.VITE_SURAASA_PLATFORM_URL
  }
  if (platform === "sso") {
    domain = import.meta.env.VITE_SSO_URL
  }
  if (platform === "itu") {
    domain = import.meta.env.VITE_ITU_PLATFORM_URL
  }

  return `${domain}${url}`
}

export const MBToBytes = (mb: number) => mb * 1000000

export const generateAuthCode = async () => {
  try {
    const res = await api.auth.generateAuthCode({
      data: { platform: USER_TYPE },
    })
    return res.code
  } catch (_) {
    return null
  }
}

export const getLearningItemURL = ({
  itemSlug,
  parentSlug,
  parentType,
  route,
  queryParams,
}: {
  itemSlug: string
  parentSlug?: string
  parentType?: string
  route: string
  queryParams?: Record<string, string>
}) => {
  let url = route.replace(":slug", itemSlug)
  if (parentSlug) {
    url = url.replace(":parentSlug", parentSlug)
  }
  if (parentType) {
    url = url.replace(":learningItemType", parentType)
  }

  if (queryParams) {
    const params = new URLSearchParams(queryParams)
    url = `${url}?${params.toString()}`
  }

  return url
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export const getITOShareReferralText = (
  link: string,
  options?: {
    discount?: boolean
    encode?: boolean
  }
) => {
  const text = `Hey! 😊

I'll be participating in the International Teachers' Olympiad 2023.
It's a fantastic opportunity for us teachers to get feedback on our teaching skills and get recognition for it!!

I found all the details in this video here: https://suraasa.co/ito-details

If you like it too, ${
    options?.discount
      ? "you can use my referral link for ₹50 discount"
      : "you can use this link to register"
  }.
${link}`

  if (options?.encode) return encodeURIComponent(text)
  return text
}

export const getITOCertificateShareText = (type: string, url: string) => {
  if (type === "international-teacher-olympiad-2023-participation") {
    return `Glad to have attempted the International Teachers’ Olympiad 2023 by Suraasa. It was a truly enriching experience to test my pedagogy skills!😊

I look forward to keep growing and learning more!

Here is a memoir of my participation: ${url}

#TrueTeachingPotential #Suraasa`
  }

  let opener
  switch (type) {
    case "international-teacher-olympiad-2023-participation":
      opener =
        "Super happy to share that I am one of the highly motivated teachers globally."
      break
    case "international-teacher-olympiad-2023-excellence":
      opener =
        "Super happy to share that I stand among the top 100 teachers globally."
      break
    case "international-teacher-olympiad-2023-merit-30-percentile":
      opener =
        "Super happy to share that I stand among the top 30% teachers globally."
      break
    case "international-teacher-olympiad-2023-merit-10-percentile":
      opener =
        "Super happy to share that I stand among the top 10% teachers globally."
      break
    case "international-teacher-olympiad-2023-merit-1-percentile":
      opener =
        "Super happy to share that I stand among the top 1% teachers globally."
      break
    default:
      break
  }

  return `${opener} Special thanks to the International Teachers’ Olympiad by Suraasa for giving all teachers such a wonderful opportunity of assessing their skills and getting celebrated for it. I am thrilled to proceed to the next level of growth in my teaching career and discover my #TrueTeachingPotential!\nCheck out my certificate: ${url}`
}

export function scrollToElementAdjusted(selector: string, offset: number) {
  const element = document.querySelector(selector)
  if (!element) return

  const elementPosition = element.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.pageYOffset - offset

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  })
}

/**
 * @param inputString "%%Bravo%%!! ";
 * @returns ["%%Bravo%%", "!!"]
 */
export function splitStringWithPattern(inputString: string) {
  const regex = /(%%[^%]+%%)\s*/
  return inputString.split(regex).filter(Boolean)
}

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const capitalizeFirstLetter = (str?: string) => {
  if (typeof str !== "string" || !str) return ""
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const humaniseBytes = (bytes: number) => {
  const megabytes = bytes / (1024 * 1024)
  const formattedMB =
    megabytes % 1 === 0 ? megabytes.toFixed(0) : megabytes.toFixed(1)

  if (megabytes < 1) {
    return `${(megabytes * 1024).toFixed(0)}KB`
  }

  return `${formattedMB}MB`
}

export const generateModuleAccessRequestLink = ({
  modules,
  type,
}: {
  modules: string[]
  type: RestrictionType
}) => {
  const moduleNameDisplay = (() => {
    if (modules.length === 1) return `The affected module is '${modules[0]}'.`

    return `The affected modules are ${modules.map(x => `'${x}'`).join(", ")}.`
  })()

  const { body, subject } = (() => {
    switch (type) {
      case RestrictionType.PAUSED: {
        const subject = encodeURIComponent("Issue with Paused Module Access")
        const body = encodeURIComponent(`Hello Suraasa Team,

I noticed that my access to ${pluralize(
          "module",
          modules.length
        )} has been paused.
${moduleNameDisplay}

Could you please help me understand the reason for this and how to restore access?
Thank you for your support!`)

        return { body, subject }
      }
      case RestrictionType.REVOKED: {
        const subject = encodeURIComponent("Issue with Revoked Module Access")
        const body = encodeURIComponent(`Hello Suraasa Team,

I noticed that my access to ${pluralize(
          "module",
          modules.length
        )} has been revoked.
${moduleNameDisplay}

Could you please help me understand the reason for this and how to restore access?
Thank you for your support!`)

        return { body, subject }
      }
      case RestrictionType.EXPIRED: {
        const subject = encodeURIComponent(
          "Request to Extend Module Access Expiry Date"
        )
        const body = encodeURIComponent(`Hello Suraasa Team,

I am reaching out to request an extension of the expiry date for ${pluralize(
          "module",
          modules.length
        )}.
${moduleNameDisplay}

Could you please assist me in extending my access to this module?
Thank you for your support!`)

        return { body, subject }
      }
    }
  })()

  return `mailto:care@suraasa.com?body=${body}&subject=${subject}`
}

export const buildPhoneNumber = ({
  countryCode,
  code,
  number,
}: {
  countryCode?: number | string | null
  code?: number | string | null
  number?: string | null
}) => {
  if (!number) return ""
  countryCode = code || countryCode
  if (!countryCode || !number) {
    return ""
  }

  let prefix = "+"
  if (countryCode.toString().includes("+")) {
    prefix = ""
  }
  return `${prefix}${countryCode}${number}`
}

export const getAssignmentSubmissionPageLink = (props: {
  courseSlug: string
  assignmentId: string | number
  submissionUuid?: string
  submissionStatus: ValueOf<typeof SUBMISSION_STATUS> | undefined
}): string => {
  const submissionId = props.submissionUuid
  const submissionStatus = props.submissionStatus
  const slug = props.courseSlug
  const assignmentId = props.assignmentId.toString()

  // Assignment Page Route
  let link = routes.assignment
    .replace(":slug", slug)
    .replace(":learningItemType", "course")
    .replace(":assignmentId", assignmentId)

  if (submissionId) {
    // Submission Page Route
    link = routes.submission
      .replace(":slug", slug)
      .replace(":learningItemType", "course")
      .replace(":assignmentId", assignmentId)
      .replace(":submissionId", submissionId)

    const isDraft = submissionStatus === SUBMISSION_STATUS.savedForLater

    if (isDraft) {
      // Assignment Page Route with prefilled submission
      link =
        routes.assignment
          .replace(":slug", slug)
          .replace(":learningItemType", "course")
          .replace(":assignmentId", assignmentId) +
        `?submissionId=${submissionId}`
    }
  }

  console.log(props, link)
  return link
}

/**
 * @param duration Format: "hh:mm:ss"
 */
export const formatDurationPrecise = (duration?: string) => {
  if (!duration) return undefined

  const [hours, minutes, seconds] = duration.split(":").map(Number)

  const h = hours
  const m = minutes
  const s = seconds

  const parts: string[] = []
  if (h > 0) parts.push(pluralize("hour", h))
  if (m > 0) parts.push(pluralize("minute", m))
  if (s > 0 && hours === 0 && m === 0) parts.push(pluralize("second", s))

  return parts.join(" ")
}

/**
 * @param duration Format: "hh:mm:ss"
 */
export const humaniseDuration = (duration?: string) => {
  if (!duration) return undefined

  const roundToNearestFive = (num: number) => Math.round(num / 5) * 5

  const [hours, minutes, seconds] = duration.split(":").map(Number)

  const h = hours
  const m = roundToNearestFive(minutes)
  const s = roundToNearestFive(seconds)

  const parts: string[] = []
  if (h > 0) parts.push(pluralize("hour", h))
  if (m > 0) parts.push(pluralize("minute", m))
  if (s > 0 && hours === 0 && m === 0) parts.push(pluralize("second", s))

  return parts.join(" ")
}

export const getTextGradientStyles = (gradient: string, shadow?: string) => ({
  background: gradient,
  display: "inline-block",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  textShadow: shadow ? "0px 8px 25px rgba(0, 0, 0, 0.15)" : undefined,
})

export const isFreeOrderResponse = (
  res: PaymentMethod[] | { isFreeOrder: boolean }
): res is { isFreeOrder: boolean } => {
  return "isFreeOrder" in res
}

export const splitFullName = (fullName: string) => {
  const names = fullName.trim().split(" ")
  const firstName = names.shift() || ""
  const lastName = names.join(" ")
  return { firstName, lastName }
}
